import { c as _c } from "react/compiler-runtime";
import type { JSX } from "react";
import { ExpertCtaStrip } from '@/components/cms/ExpertCtaStrip';
import { HotboxCtaStrip } from '@/components/cms/HotboxCtaStrip';
import type { DescriptionBlock, MediaTextBlock } from './types';
import { Accordion } from './blocks/Accordion';
import { BirdeyeWidget } from './blocks/BirdeyeWidget';
import { BulletList } from './blocks/BulletList';
import { Callout } from './blocks/Callout';
import { ContentWithSidebar } from './blocks/ContentWithSidebar';
import { Cta } from './blocks/Cta';
import { Divider } from './blocks/Divider';
import { Faq } from './blocks/Faq';
import { FeatureGrid } from './blocks/FeatureGrid';
import { Heading } from './blocks/Heading';
import { HeroBanner } from './blocks/HeroBanner';
import { HubSpotCta } from './blocks/HubSpotCta';
import { HubSpotForm } from './blocks/HubSpotForm';
import { Image } from './blocks/Image';
import { LegacyInlineFormatting } from './blocks/LegacyInlineFormatting';
import { LightboxGallery } from './blocks/LightboxGallery';
import { MediaText } from './blocks/MediaText';
import { OrderedList } from './blocks/OrderedList';
import { Panel } from './blocks/Panel';
import { Paragraph } from './blocks/Paragraph';
import { ResponsiveImage } from './blocks/ResponsiveImage';
import { RichHtml } from './blocks/RichHtml';
import { Table } from './blocks/Table';
import { Tabs } from './blocks/Tabs';
import { ThankYouEmbed } from './blocks/ThankYouEmbed';
import { Video } from './blocks/Video';
import styles from './DescriptionBlocks.module.css';
interface DescriptionBlocksProps {
  blocks: DescriptionBlock[];
  /**
   * Stable prefix used to derive each block's `data-cms-block-id`. Top-level
   * call passes `<route>.description`; nested renders (inside Tabs / Accordion
   * / Faq) append their own segment.
   */
  blockIdPrefix: string;
  className?: string;
}
export function DescriptionBlocks(t0) {
  const $ = _c(8);
  const {
    blocks,
    blockIdPrefix,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = [styles.region, className].filter(Boolean);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1.join(" ");
  let t3;
  if ($[2] !== blockIdPrefix || $[3] !== blocks) {
    t3 = renderBlockNodes(blocks, blockIdPrefix);
    $[2] = blockIdPrefix;
    $[3] = blocks;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className={t2} data-cms-region="description">{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function renderBlockNodes(blocks: DescriptionBlock[], blockIdPrefix: string): JSX.Element[] {
  const nodes: JSX.Element[] = [];
  for (let idx = 0; idx < blocks.length; idx += 1) {
    const block = blocks[idx];
    const nextBlock = blocks[idx + 1];
    if (block && nextBlock && isListingTitleHeading(block) && isVideoMediaText(nextBlock)) {
      const combinedBlock: MediaTextBlock = {
        ...nextBlock,
        variant: nextBlock.variant ?? 'jumbotronVideo',
        content: [block, ...nextBlock.content]
      };
      const blockId = `${blockIdPrefix}.${idx}.mediaText`;
      nodes.push(<BlockNode key={blockId} block={combinedBlock} blockId={blockId} />);
      idx += 1;
      continue;
    }
    if (block) {
      const blockId = `${blockIdPrefix}.${idx}.${block.kind}`;
      nodes.push(<BlockNode key={blockId} block={block} blockId={blockId} />);
    }
  }
  return nodes;
}
function isListingTitleHeading(block: DescriptionBlock): boolean {
  return block.kind === 'heading' && block.variant === 'listingTitle';
}
function isVideoMediaText(block: DescriptionBlock): block is MediaTextBlock {
  return block.kind === 'mediaText' && block.media.some(mediaBlock => mediaBlock.kind === 'video');
}
function BlockNode(t0) {
  const $ = _c(85);
  const {
    block,
    blockId
  } = t0;
  switch (block.kind) {
    case "heroBanner":
      {
        let t1;
        if ($[0] !== block || $[1] !== blockId) {
          t1 = <HeroBanner block={block} blockId={blockId} />;
          $[0] = block;
          $[1] = blockId;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case "heading":
      {
        let t1;
        if ($[3] !== block || $[4] !== blockId) {
          t1 = <Heading block={block} blockId={blockId} />;
          $[3] = block;
          $[4] = blockId;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "paragraph":
      {
        let t1;
        if ($[6] !== block || $[7] !== blockId) {
          t1 = <Paragraph block={block} blockId={blockId} />;
          $[6] = block;
          $[7] = blockId;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    case "bulletList":
      {
        let t1;
        if ($[9] !== block || $[10] !== blockId) {
          t1 = <BulletList block={block} blockId={blockId} />;
          $[9] = block;
          $[10] = blockId;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        return t1;
      }
    case "orderedList":
      {
        let t1;
        if ($[12] !== block || $[13] !== blockId) {
          t1 = <OrderedList block={block} blockId={blockId} />;
          $[12] = block;
          $[13] = blockId;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
    case "image":
      {
        let t1;
        if ($[15] !== block || $[16] !== blockId) {
          t1 = <Image block={block} blockId={blockId} />;
          $[15] = block;
          $[16] = blockId;
          $[17] = t1;
        } else {
          t1 = $[17];
        }
        return t1;
      }
    case "responsiveImage":
      {
        let t1;
        if ($[18] !== block || $[19] !== blockId) {
          t1 = <ResponsiveImage block={block} blockId={blockId} />;
          $[18] = block;
          $[19] = blockId;
          $[20] = t1;
        } else {
          t1 = $[20];
        }
        return t1;
      }
    case "table":
      {
        let t1;
        if ($[21] !== block || $[22] !== blockId) {
          t1 = <Table block={block} blockId={blockId} />;
          $[21] = block;
          $[22] = blockId;
          $[23] = t1;
        } else {
          t1 = $[23];
        }
        return t1;
      }
    case "callout":
      {
        let t1;
        if ($[24] !== block || $[25] !== blockId) {
          t1 = <Callout block={block} blockId={blockId} />;
          $[24] = block;
          $[25] = blockId;
          $[26] = t1;
        } else {
          t1 = $[26];
        }
        return t1;
      }
    case "faq":
      {
        let t1;
        if ($[27] !== block || $[28] !== blockId) {
          t1 = <Faq block={block} blockId={blockId} />;
          $[27] = block;
          $[28] = blockId;
          $[29] = t1;
        } else {
          t1 = $[29];
        }
        return t1;
      }
    case "cta":
      {
        let t1;
        if ($[30] !== block || $[31] !== blockId) {
          t1 = <Cta block={block} blockId={blockId} />;
          $[30] = block;
          $[31] = blockId;
          $[32] = t1;
        } else {
          t1 = $[32];
        }
        return t1;
      }
    case "featureGrid":
      {
        let t1;
        if ($[33] !== block || $[34] !== blockId) {
          t1 = <FeatureGrid block={block} blockId={blockId} />;
          $[33] = block;
          $[34] = blockId;
          $[35] = t1;
        } else {
          t1 = $[35];
        }
        return t1;
      }
    case "contentWithSidebar":
      {
        let t1;
        if ($[36] !== block || $[37] !== blockId) {
          t1 = <ContentWithSidebar block={block} blockId={blockId} />;
          $[36] = block;
          $[37] = blockId;
          $[38] = t1;
        } else {
          t1 = $[38];
        }
        return t1;
      }
    case "panel":
      {
        let t1;
        if ($[39] !== block || $[40] !== blockId) {
          t1 = <Panel block={block} blockId={blockId} />;
          $[39] = block;
          $[40] = blockId;
          $[41] = t1;
        } else {
          t1 = $[41];
        }
        return t1;
      }
    case "mediaText":
      {
        let t1;
        if ($[42] !== block || $[43] !== blockId) {
          t1 = <MediaText block={block} blockId={blockId} />;
          $[42] = block;
          $[43] = blockId;
          $[44] = t1;
        } else {
          t1 = $[44];
        }
        return t1;
      }
    case "hubspotForm":
      {
        let t1;
        if ($[45] !== block || $[46] !== blockId) {
          t1 = <HubSpotForm block={block} blockId={blockId} />;
          $[45] = block;
          $[46] = blockId;
          $[47] = t1;
        } else {
          t1 = $[47];
        }
        return t1;
      }
    case "hubspotCta":
      {
        let t1;
        if ($[48] !== block || $[49] !== blockId) {
          t1 = <HubSpotCta block={block} blockId={blockId} />;
          $[48] = block;
          $[49] = blockId;
          $[50] = t1;
        } else {
          t1 = $[50];
        }
        return t1;
      }
    case "thankYouEmbed":
      {
        let t1;
        if ($[51] !== block || $[52] !== blockId) {
          t1 = <ThankYouEmbed block={block} blockId={blockId} />;
          $[51] = block;
          $[52] = blockId;
          $[53] = t1;
        } else {
          t1 = $[53];
        }
        return t1;
      }
    case "hotboxCtaStrip":
      {
        let t1;
        if ($[54] !== block.config || $[55] !== block.variant) {
          t1 = <HotboxCtaStrip config={block.config} variant={block.variant} />;
          $[54] = block.config;
          $[55] = block.variant;
          $[56] = t1;
        } else {
          t1 = $[56];
        }
        return t1;
      }
    case "expertCtaStrip":
      {
        let t1;
        if ($[57] !== block.config) {
          t1 = <ExpertCtaStrip config={block.config} />;
          $[57] = block.config;
          $[58] = t1;
        } else {
          t1 = $[58];
        }
        return t1;
      }
    case "birdeyeWidget":
      {
        let t1;
        if ($[59] !== block || $[60] !== blockId) {
          t1 = <BirdeyeWidget block={block} blockId={blockId} />;
          $[59] = block;
          $[60] = blockId;
          $[61] = t1;
        } else {
          t1 = $[61];
        }
        return t1;
      }
    case "video":
      {
        let t1;
        if ($[62] !== block || $[63] !== blockId) {
          t1 = <Video block={block} blockId={blockId} />;
          $[62] = block;
          $[63] = blockId;
          $[64] = t1;
        } else {
          t1 = $[64];
        }
        return t1;
      }
    case "lightboxGallery":
      {
        let t1;
        if ($[65] !== block || $[66] !== blockId) {
          t1 = <LightboxGallery block={block} blockId={blockId} />;
          $[65] = block;
          $[66] = blockId;
          $[67] = t1;
        } else {
          t1 = $[67];
        }
        return t1;
      }
    case "tabs":
      {
        let t1;
        if ($[68] !== block || $[69] !== blockId) {
          t1 = <Tabs block={block} blockId={blockId} />;
          $[68] = block;
          $[69] = blockId;
          $[70] = t1;
        } else {
          t1 = $[70];
        }
        return t1;
      }
    case "accordion":
      {
        let t1;
        if ($[71] !== block || $[72] !== blockId) {
          t1 = <Accordion block={block} blockId={blockId} />;
          $[71] = block;
          $[72] = blockId;
          $[73] = t1;
        } else {
          t1 = $[73];
        }
        return t1;
      }
    case "legacyInlineFormatting":
      {
        let t1;
        if ($[74] !== block || $[75] !== blockId) {
          t1 = <LegacyInlineFormatting block={block} blockId={blockId} />;
          $[74] = block;
          $[75] = blockId;
          $[76] = t1;
        } else {
          t1 = $[76];
        }
        return t1;
      }
    case "richHtml":
      {
        let t1;
        if ($[77] !== block || $[78] !== blockId) {
          t1 = <RichHtml block={block} blockId={blockId} />;
          $[77] = block;
          $[78] = blockId;
          $[79] = t1;
        } else {
          t1 = $[79];
        }
        return t1;
      }
    case "divider":
      {
        let t1;
        if ($[80] !== block || $[81] !== blockId) {
          t1 = <Divider block={block} blockId={blockId} />;
          $[80] = block;
          $[81] = blockId;
          $[82] = t1;
        } else {
          t1 = $[82];
        }
        return t1;
      }
    default:
      {
        const exhaustive = block;
        const t1 = String(exhaustive);
        let t2;
        if ($[83] !== t1) {
          t2 = <>{t1}</>;
          $[83] = t1;
          $[84] = t2;
        } else {
          t2 = $[84];
        }
        return t2;
      }
  }
}